/**
 * 错误处理工具（包装版）
 * 
 * 版本: 1.1.0
 * 变更记录:
 * - 2025-09-29: 新增包装式实现，链式调用不破坏其他 console 包装；增加幂等安装防护
 * - 2024-08-18: 初始版本（参考 errorHandlers.ts 实现思路）
 */

// 用于忽略的错误消息模式（保持与旧实现一致并可按需缩窄）
const ignoredErrorPatterns = [
  // 消息端口错误
  'The message port closed before a response was received',
  'Extension context invalidated',

  // Manifest 相关错误
  'Error while trying to use the following icon from the Manifest',
  "Download error or resource isn't a valid image",
  'Failed to load resource: the server responded with a status of 404',
  'Failed to load resource: net::ERR_FILE_NOT_FOUND'
];

/**
 * 安装错误处理（包装当前 console 方法，支持与其他包装（如 consoleSecurity）叠加）
 * 幂等：重复调用不会重复安装。
 */
export const setupErrorHandlers = () => {
  if (typeof window !== 'undefined') {
    const w = window as any;
    if (w.__errorHandlersInstalled_v2) return;
    w.__errorHandlersInstalled_v2 = true;
  }

  // 注意：保存“当前”的 console 方法（可能已经被其他模块包装过）
  const prevConsoleError = console.error;
  const prevConsoleWarn = console.warn;

  console.error = function (...args: any[]) {
    if (args.length > 0 && shouldIgnoreError(args[0])) return;
    prevConsoleError.apply(console, args);
  };

  console.warn = function (...args: any[]) {
    if (args.length > 0 && shouldIgnoreError(args[0])) return;
    prevConsoleWarn.apply(console, args);
  };

  if (typeof window !== 'undefined') {
    // 捕获普通错误（捕获阶段）
    window.addEventListener(
      'error',
      (event) => {
        if ((event as any).error && shouldIgnoreError((event as any).error)) {
          event.preventDefault();
          return false as any;
        }
        return true as any;
      },
      true
    );

    // 捕获资源加载错误（img/script/link 等）
    window.addEventListener(
      'error',
      (event) => {
        const target = event.target as HTMLElement;
        if (target && target.tagName) {
          const tagName = target.tagName.toLowerCase();
          if (['img', 'script', 'link'].includes(tagName)) {
            if (shouldIgnoreResourceError(event as any)) {
              event.preventDefault();
              return false as any;
            }
          }
        }
        return true as any;
      },
      true
    );

    // 捕获未处理的 Promise 拒绝
    window.addEventListener('unhandledrejection', (event) => {
      if ((event as any).reason && shouldIgnoreError((event as any).reason)) {
        event.preventDefault();
        return false as any;
      }
      return true as any;
    });
  }
};

/** 检查是否应该忽略的错误 */
const shouldIgnoreError = (error: any): boolean => {
  if (typeof error === 'string') {
    return ignoredErrorPatterns.some((pattern) => error.includes(pattern));
  }
  if (error instanceof Error) {
    return ignoredErrorPatterns.some((pattern) => error.message.includes(pattern));
  }
  if (typeof error === 'object' && error !== null) {
    try {
      const errorStr = JSON.stringify(error);
      return ignoredErrorPatterns.some((pattern) => errorStr.includes(pattern));
    } catch {
      return false;
    }
  }
  return false;
};

/** 检查是否应该忽略的资源加载错误 */
const shouldIgnoreResourceError = (event: ErrorEvent): boolean => {
  const target = event.target as HTMLElement & { src?: string; href?: string };
  if (!target) return false;
  const resourceUrl = target.src || target.href || '';
  const isManifestIconError =
    resourceUrl.includes('logo192.png') ||
    resourceUrl.includes('logo512.png') ||
    resourceUrl.includes('apple-touch-icon');
  return isManifestIconError;
};
